import { useState } from 'react';
import { usePreferences } from '../../context/usePreferences';
import { MyNewsTabs } from './MyNewsTabs';
import { FeedView } from './FeedView';
import { FollowingView } from './FollowingView';
import { useTabPager } from './useTabPager';
import { buildSectionsQuery } from '../../lib/cmsSections';
import './MyNewsPage.css';

const RECOMMENDED_TAB = 'Recommended';
const FOLLOWING_TAB = 'Following';

// Recommended is always first; Following appears when switched on in the
// sheet; the user's sections follow in their chosen order.
function buildTabs({ sections, followingEnabled }) {
  return [
    RECOMMENDED_TAB,
    ...(followingEnabled ? [FOLLOWING_TAB] : []),
    ...sections,
  ];
}

// Each section tab is a story-feed query plus the layout that suits it.
// (Following builds its own feed: see FollowingView.)
function buildFeed(tab, sections) {
  const forSections = tab === RECOMMENDED_TAB ? sections : [tab];

  return {
    query: buildSectionsQuery(forSections),
    layout: tab === RECOMMENDED_TAB ? 'list' : 'cards',
    emptyMessage: 'Add sections to personalise your feed.',
  };
}

// Tab names become the panel ids that aria-controls points at.
const panelId = (tab) =>
  `my-news-panel-${tab.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;

export function MyNewsPage() {
  const { sections, followingEnabled, open } = usePreferences();
  const [selectedTab, setSelectedTab] = useState(RECOMMENDED_TAB);

  const tabs = buildTabs({ sections, followingEnabled });
  // Fall back to Recommended when the selected tab's section is removed, or
  // when the Following tab is switched off.
  const activeTab = tabs.includes(selectedTab) ? selectedTab : RECOMMENDED_TAB;
  const activeIndex = tabs.indexOf(activeTab);

  const pager = useTabPager({
    count: tabs.length,
    index: activeIndex,
    onChange: (index) => setSelectedTab(tabs[index]),
  });

  return (
    <main className="my-news" role="main">
      <MyNewsTabs
        tabs={tabs}
        activeTab={activeTab}
        onSelect={setSelectedTab}
        onAdd={open}
        panelIdFor={panelId}
      />

      <div
        className="my-news__viewport"
        ref={pager.viewportRef}
        {...pager.handlers}
      >
        <div
          className="my-news__track"
          ref={pager.trackRef}
          style={pager.trackStyle}
        >
          {tabs.map((tab, index) => {
            // Only the active panel and its neighbours are live: enough that
            // a drag always reveals real content, without every tab fetching
            // its feed up front.
            const isNear = Math.abs(index - activeIndex) <= 1;
            if (!isNear) return <div key={tab} className="my-news__panel" />;

            if (tab === FOLLOWING_TAB) {
              return (
                <div key={tab} className="my-news__panel">
                  <FollowingView id={panelId(tab)} title={tab} />
                </div>
              );
            }

            const feed = buildFeed(tab, sections);
            return (
              <div key={tab} className="my-news__panel">
                <FeedView
                  id={panelId(tab)}
                  title={tab}
                  query={feed.query}
                  layout={feed.layout}
                  emptyMessage={feed.emptyMessage}
                />
              </div>
            );
          })}
        </div>
      </div>
    </main>
  );
}
